import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  MenuItem,
} from '@mui/material';

const teamSizeOptions = [
  { value: '1-5', label: '1–5 members' },
  { value: '6-15', label: '6–15 members' },
  { value: '16-50', label: '16–50 members' },
  { value: '50+', label: '50+ members' },
];

const MultiTeamHubPage: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    companyName: '',
    teamSize: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/multi-team-hub', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          full_name: formData.fullName,
          email: formData.email,
          company_name: formData.companyName || null,
          team_size: formData.teamSize || null,
          message: formData.message || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit sign-up');
      }

      setSuccess(true);
      setFormData({
        fullName: '',
        email: '',
        companyName: '',
        teamSize: '',
        message: '',
      });
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          borderBottom: '4px solid #dc143c',
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                bgcolor: '#dc143c',
                px: 3,
                py: 1,
                mb: 3,
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontFamily: '"Impact", "Arial Black", sans-serif',
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '4rem', lg: '4.5rem' },
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: '#1a1a1a',
                  lineHeight: 1.1,
                }}
              >
                MULTI TEAM HUB
              </Typography>
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Impact", "Arial Black", sans-serif',
                fontSize: { xs: '1.5rem', md: '2rem' },
                fontWeight: 900,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#1a1a1a',
                mb: 3,
              }}
            >
              JOIN THE BETA
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '1rem', md: '1.2rem' },
                lineHeight: 1.8,
                color: '#555555',
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              Multi Team Hub brings together Microsoft Teams, Slack, Google Chat,
              and Discord into one unified platform. Get all your notifications,
              messages, and team communications in a single place — no more
              switching between apps. Sign up below to get early access to the
              beta and help shape the future of team communication.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Form Section */}
      <Container maxWidth="xl">
        <Box
          sx={{
            maxWidth: '640px',
            mx: 'auto',
            py: { xs: 6, md: 10 },
          }}
        >
          {/* Success State */}
          {success && (
            <Alert
              severity="success"
              sx={{
                mb: 4,
                borderRadius: 0,
                border: '2px solid #4caf50',
                bgcolor: 'rgba(76, 175, 80, 0.08)',
                color: '#1a1a1a',
                '& .MuiAlert-icon': { color: '#4caf50' },
              }}
            >
              You're on the list! We'll be in touch when beta access is available.
            </Alert>
          )}

          {/* Error State */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 4,
                borderRadius: 0,
                border: '2px solid #dc143c',
                bgcolor: 'rgba(220, 20, 60, 0.08)',
                color: '#1a1a1a',
                '& .MuiAlert-icon': { color: '#dc143c' },
              }}
            >
              {error}
            </Alert>
          )}

          {!success && (
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                name="fullName"
                placeholder="Full Name *"
                variant="standard"
                value={formData.fullName}
                onChange={handleChange}
                required
                sx={{
                  mb: 4,
                  '& .MuiInput-root': {
                    fontSize: '1.125rem',
                    color: '#1a1a1a',
                  },
                  '& .MuiInput-root:before': {
                    borderBottomColor: '#cccccc',
                  },
                  '& .MuiInput-root:hover:not(.Mui-disabled):before': {
                    borderBottomColor: '#dc143c',
                  },
                  '& .MuiInput-root:after': {
                    borderBottomColor: '#dc143c',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#888',
                    opacity: 1,
                  },
                }}
              />

              <TextField
                fullWidth
                name="email"
                type="email"
                placeholder="Email *"
                variant="standard"
                value={formData.email}
                onChange={handleChange}
                required
                sx={{
                  mb: 4,
                  '& .MuiInput-root': {
                    fontSize: '1.125rem',
                    color: '#1a1a1a',
                  },
                  '& .MuiInput-root:before': {
                    borderBottomColor: '#cccccc',
                  },
                  '& .MuiInput-root:hover:not(.Mui-disabled):before': {
                    borderBottomColor: '#dc143c',
                  },
                  '& .MuiInput-root:after': {
                    borderBottomColor: '#dc143c',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#888',
                    opacity: 1,
                  },
                }}
              />

              <TextField
                fullWidth
                name="companyName"
                placeholder="Company / Organization (optional)"
                variant="standard"
                value={formData.companyName}
                onChange={handleChange}
                sx={{
                  mb: 4,
                  '& .MuiInput-root': {
                    fontSize: '1.125rem',
                    color: '#1a1a1a',
                  },
                  '& .MuiInput-root:before': {
                    borderBottomColor: '#cccccc',
                  },
                  '& .MuiInput-root:hover:not(.Mui-disabled):before': {
                    borderBottomColor: '#dc143c',
                  },
                  '& .MuiInput-root:after': {
                    borderBottomColor: '#dc143c',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#888',
                    opacity: 1,
                  },
                }}
              />

              <TextField
                fullWidth
                name="teamSize"
                select
                label="Team Size (optional)"
                variant="standard"
                value={formData.teamSize}
                onChange={handleChange}
                sx={{
                  mb: 4,
                  '& .MuiInputLabel-root': {
                    color: '#888',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#dc143c',
                  },
                  '& .MuiInput-root': {
                    fontSize: '1.125rem',
                    color: '#1a1a1a',
                  },
                  '& .MuiInput-root:before': {
                    borderBottomColor: '#cccccc',
                  },
                  '& .MuiInput-root:hover:not(.Mui-disabled):before': {
                    borderBottomColor: '#dc143c',
                  },
                  '& .MuiInput-root:after': {
                    borderBottomColor: '#dc143c',
                  },
                  '& .MuiSelect-icon': {
                    color: '#1a1a1a',
                  },
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        backgroundColor: '#1a1a1a',
                        '& .MuiMenuItem-root': {
                          color: '#ffffff',
                          '&:hover': {
                            backgroundColor: '#333333',
                          },
                          '&.Mui-selected': {
                            backgroundColor: '#333333',
                            color: '#ffffff',
                          },
                          '&.Mui-selected:hover': {
                            backgroundColor: '#444444',
                          },
                        },
                      },
                    },
                  },
                }}
              >
                {teamSizeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                name="message"
                placeholder="Why are you interested in Multi Team Hub? (optional)"
                variant="standard"
                multiline
                rows={3}
                value={formData.message}
                onChange={handleChange}
                sx={{
                  mb: 5,
                  '& .MuiInput-root': {
                    fontSize: '1.125rem',
                    color: '#1a1a1a',
                  },
                  '& .MuiInput-root:before': {
                    borderBottomColor: '#cccccc',
                  },
                  '& .MuiInput-root:hover:not(.Mui-disabled):before': {
                    borderBottomColor: '#dc143c',
                  },
                  '& .MuiInput-root:after': {
                    borderBottomColor: '#dc143c',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#888',
                    opacity: 1,
                  },
                }}
              />

              <Box sx={{ textAlign: 'center' }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    bgcolor: '#dc143c',
                    color: '#ffffff',
                    padding: '12px 32px',
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderRadius: 0,
                    boxShadow: 'none',
                    border: '3px solid #dc143c',
                    '&:hover': {
                      bgcolor: '#b01030',
                      borderColor: '#b01030',
                      boxShadow: 'none',
                      transform: 'translateY(-2px)',
                    },
                    '&:disabled': {
                      bgcolor: '#555',
                      borderColor: '#555',
                      color: '#999',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: '#ffffff' }} />
                  ) : (
                    'Request Beta Access'
                  )}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default MultiTeamHubPage;
